import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Grid,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import {
  fetchCurrentUserProfile,
  getEmployeesByType,
  sendPayslip,
  getAllBankAccounts,
  getSalaryMetricsData,
  sendPayslipBankId,
} from "../api/services";
import { AuthContext } from "../context/auth-context";
import {
  TrendingUp,
  AccountBalance,
  Receipt,
  CalendarToday,
  Send,
  Refresh,
  CheckCircle,
  BusinessCenter,
  Home,
  LocalHospital,
  MonetizationOn,
  RequestQuote,
} from "@mui/icons-material";

const SalaryMetricsChart = () => {
  const { user } = useContext(AuthContext);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get current year and month
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
  
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [employeeType, setEmployeeType] = useState(null);
  const [hasSalaryData, setHasSalaryData] = useState(true);

  const [selectedBankAccount, setSelectedBankAccount] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Generate years from 2020 to current year + 1
  const getYears = () => {
    const years = [];
    for (let year = 2020; year <= currentYear + 1; year++) {
      years.push(year);
    }
    return years;
  };

  const getMonths = () => {
    return [
      { value: "01", label: "January" },
      { value: "02", label: "February" },
      { value: "03", label: "March" },
      { value: "04", label: "April" },
      { value: "05", label: "May" },
      { value: "06", label: "June" },
      { value: "07", label: "July" },
      { value: "08", label: "August" },
      { value: "09", label: "September" },
      { value: "10", label: "October" },
      { value: "11", label: "November" },
      { value: "12", label: "December" },
    ];
  };

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        setBankAccountsLoading(true);
        const accounts = await getAllBankAccounts();
        setBankAccounts(accounts);
        
        if (accounts.length > 0) {
          setSelectedBankAccount(accounts[0].id);
        }
      } catch (err) {
        console.error("Error fetching bank accounts:", err);
      } finally {
        setBankAccountsLoading(false);
      }
    };

    if (user) fetchBankAccounts();
  }, [user]);

  const handleSalaryError = (error) => {
    console.error('Salary metrics error:', error);
    
    if (error?.response?.status === 404 || error?.code === 'ERR_BAD_REQUEST') {
      setHasSalaryData(false);
      setError(null);
      setMetrics(null);
      return;
    }
    
    if (error?.code === 'ERR_NETWORK') {
      const message = "Unable to connect to server. Please check your internet connection and try again.";
      setError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    if (error?.response?.status === 403) {
      const message = "You don't have permission to view salary data. Please contact your administrator.";
      setError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    if (error?.response?.status === 401) {
      const message = "Your session has expired. Please log in again.";
      setError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    if (error?.response?.status >= 500) {
      const message = "Server is currently unavailable. Please try again later.";
      setError(message);
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    const apiMessage = error?.response?.data?.message || error?.message;
    if (apiMessage) {
      const userFriendlyMessage = getUserFriendlyErrorMessage(apiMessage);
      setError(userFriendlyMessage);
      setSnackbarMessage(userFriendlyMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    const fallbackMessage = "Unable to load salary data. Please try again later.";
    setError(fallbackMessage);
    setSnackbarMessage(fallbackMessage);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const getUserFriendlyErrorMessage = (errorMessage) => {
    switch (errorMessage) {
      case "Employee ID not found":
        return "We couldn't find your employee profile. Please contact HR.";
      case "Invalid role: Must be EMPLOYEE, CONSULTANT, or INTERN":
        return "Your account role needs configuration. Please contact support.";
      case "Employee data not found":
        return "Employee information not available. Please try again later.";
      case "Invalid API response: data or data.data is undefined":
        return "Temporary data issue. Please try again in a few moments.";
      default:
        return "We're experiencing technical difficulties. Please try again later.";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setHasSalaryData(true);
        setError(null);

        const userProfile = await fetchCurrentUserProfile();
        const employeeId = userProfile.employeeId;
        if (!employeeId) throw new Error("Employee ID not found");

        const validRoles = ["EMPLOYEE", "CONSULTANT", "INTERN"];
        if (!userProfile.role || !validRoles.includes(userProfile.role)) {
          throw new Error("Invalid role: Must be EMPLOYEE, CONSULTANT, or INTERN");
        }
        setEmployeeType(userProfile.role);

        const employeeData = await getEmployeesByType(userProfile.role);
        const employee = employeeData.find(emp => emp.employeeId === employeeId);
        if (!employee) throw new Error("Employee data not found");

        if (selectedBankAccount) {
          const period = `${selectedMonth}/${selectedYear}`;

          const response = await getSalaryMetricsData(selectedBankAccount, period);
          
          if (!response || !response.data || response.data.length === 0) {
            setHasSalaryData(false);
            setMetrics(null);
            return;
          }

          const salaryData = response.data[0];

          const calculatedMetrics = {
            GrossPay: salaryData.grossPay || 0,
            GrossPayPercentage: 100.00,
            Bonus: salaryData.bonus || 0,
            BonusPercentage: salaryData.bonusPercentage || 0,
            PAYE: salaryData.tax || 0,
            PAYEPercentage: salaryData.taxPercentage || 0,
            NSSF: salaryData.nssf || 0,
            NSSFPercentage: salaryData.nssfPercentage || 0,
            HousingLevy: salaryData.housingLevy || 0,
            HousingLevyPercentage: salaryData.housingLevyPercentage || 0,
            SHA: salaryData.sha || 0,
            SHAPercentage: salaryData.shaPercentage || 0,
            WithholdingTax: salaryData.withholdingTax || 0,
            WithholdingTaxPercentage: salaryData.withholdingTaxPercentage || 0,
            NetPay: salaryData.netPay || 0,
            NetPayPercentage: salaryData.netPayPercentage || 0,
          };
          
          setMetrics(calculatedMetrics);
          setHasSalaryData(true);
          setError(null);
        }
      } catch (err) {
        handleSalaryError(err);
      } finally {
        setLoading(false);
      }
    };

    if (user && selectedBankAccount) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedYear, selectedMonth, selectedBankAccount]);

  // Prepare data for recharts pie chart
  const getChartData = () => {
    if (!metrics) return [];
    const data = [];
    
    // Map display names with proper formatting
    const displayNames = {
      GrossPay: 'Gross Pay',
      Bonus: 'Bonus',
      PAYE: 'PAYE',
      NSSF: 'NSSF',
      HousingLevy: 'Housing Levy',
      SHA: 'SHA',
      WithholdingTax: 'Withholding Tax',
      NetPay: 'Net Pay'
    };
    
    const colors = ['#4caf50', '#ff9800', '#f44336', '#9c27b0', '#3f51b5', '#00bcd4', '#d32f2f', '#2e7d32'];
    const metricKeys = ['GrossPay', 'Bonus', 'PAYE', 'NSSF', 'HousingLevy', 'SHA', 'WithholdingTax', 'NetPay'];
    
    metricKeys.forEach((key, index) => {
      const percentageKey = `${key}Percentage`;
      if (metrics[percentageKey] !== null && metrics[percentageKey] !== undefined && metrics[percentageKey] > 0) {
        data.push({
          name: displayNames[key] || key,
          value: metrics[percentageKey],
          amount: metrics[key] || 0,
          color: colors[index],
        });
      }
    });
    return data;
  };

  const chartData = getChartData();

  // Custom tooltip for recharts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <Typography variant="body2" fontWeight="bold">{data.name}</Typography>
          <Typography variant="body2">Percentage: {data.value}%</Typography>
          <Typography variant="body2">Amount: KSh {data.amount.toLocaleString()}</Typography>
        </Paper>
      );
    }
    return null;
  };

  const handleSendPayslip = async () => {
    try {
      setLoading(true);
      
      if (selectedBankAccount) {
        await sendPayslipBankId(selectedBankAccount);
      } else {
        const userProfile = await fetchCurrentUserProfile();
        const employeeId = userProfile.employeeId;
        if (!employeeId) throw new Error("Employee ID not found");

        await sendPayslip(employeeId);
      }
      
      setSnackbarMessage("Your payslip has been sent to your registered email.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error sending payslip:", err);
      
      if (err?.response?.status === 404) {
        setSnackbarMessage("No payslip available for the selected period.");
        setSnackbarSeverity("info");
      } else if (err?.response?.status === 400 && err?.response?.data?.apiErrorDto?.errorMessage?.includes("Payroll not found for bankId")) {
        setSnackbarMessage("No payroll data found for the selected bank account.");
        setSnackbarSeverity("warning");
      } else {
        setSnackbarMessage("Failed to send payslip. Please try again later.");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `KSh ${value.toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `${value}%`;
  };

  // Get color for metric based on value
  const getMetricColor = (key) => {
    if (key === 'Net Pay') return '#2e7d32';
    if (key === 'Gross Pay') return '#009db9';
    if (key === 'Bonus') return '#ff9800';
    if (key === 'PAYE') return '#f44336';
    if (key === 'NSSF') return '#9c27b0';
    if (key === 'Housing Levy') return '#3f51b5';
    if (key === 'SHA') return '#00bcd4';
    if (key === 'Withholding Tax') return '#d32f2f';
    return '#666';
  };

  // Get icon for metric with realistic icons
  const getMetricIcon = (key) => {
    if (key === 'Net Pay') return <MonetizationOn />;
    if (key === 'Gross Pay') return <AccountBalance />;
    if (key === 'Bonus') return <TrendingUp />;
    if (key === 'PAYE') return <Receipt />;
    if (key === 'NSSF') return <BusinessCenter />;
    if (key === 'Housing Levy') return <Home />;
    if (key === 'SHA') return <LocalHospital />;
    if (key === 'Withholding Tax') return <RequestQuote />;
    return <Receipt />;
  };

  // Get color for metric background
  const getMetricBgColor = (key) => {
    if (key === 'Net Pay') return '#e8f5e9';
    if (key === 'Gross Pay') return '#e0f7fa';
    if (key === 'Bonus') return '#fff3e0';
    if (key === 'PAYE') return '#ffebee';
    if (key === 'NSSF') return '#f3e5f5';
    if (key === 'Housing Levy') return '#e8eaf6';
    if (key === 'SHA') return '#e0f7fa';
    if (key === 'Withholding Tax') return '#ffebee';
    return '#f5f5f5';
  };

  const monthName = getMonths().find(m => m.value === selectedMonth)?.label || '';
  const formattedPeriod = `${monthName} ${selectedYear}`;

  // Metric cards to display with proper display names
  const metricKeys = [
    { key: 'GrossPay', label: 'Gross Pay' },
    { key: 'Bonus', label: 'Bonus' },
    { key: 'PAYE', label: 'PAYE' },
    { key: 'NSSF', label: 'NSSF' },
    { key: 'HousingLevy', label: 'Housing Levy' },
    { key: 'SHA', label: 'SHA' },
    { key: 'WithholdingTax', label: 'Withholding Tax' },
    { key: 'NetPay', label: 'Net Pay' }
  ];

  // Colors for pie chart
  const COLORS = ['#4caf50', '#ff9800', '#f44336', '#9c27b0', '#3f51b5', '#00bcd4', '#d32f2f', '#2e7d32'];

  // Custom label for pie chart - cleaner look with better positioning
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Only show label if percentage is significant enough
    const percentage = (percent * 100);
    if (percentage < 3) return null;

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '0 1px 3px rgba(0,0,0,0.3)'
        }}
      >
        {`${percentage.toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f5f7fa', 
      py: { xs: 2, sm: 3, md: 4 },
      px: { xs: 1, sm: 2, md: 3 },
    }}>
      <Box sx={{ 
        maxWidth: "1200px",
        width: "100%",
        margin: "0 auto",
        pb: 4,
      }}>
        {/* Header with Glass Effect */}
        <Card
          sx={{
            mt: { xs: 8, sm: 9, md: 10 },
            mb: 3,
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #009db9 0%, #00707f 100%)',
            color: '#fff',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, sm: 3.5, md: 4 },
            boxShadow: '0 12px 40px rgba(0,157,185,0.25)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            top: -100, 
            right: -100, 
            width: 300, 
            height: 300, 
            borderRadius: '50%', 
            background: 'rgba(255,255,255,0.05)' 
          }} />
          <Box sx={{ 
            position: 'absolute', 
            bottom: -50, 
            left: -50, 
            width: 200, 
            height: 200, 
            borderRadius: '50%', 
            background: 'rgba(255,255,255,0.03)' 
          }} />
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2, fontSize: '0.75rem' }}>
                Salary Overview
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                {formattedPeriod}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                {employeeType || 'Employee'} • {bankAccounts.find(a => a.id === selectedBankAccount)?.bankName || 'Select Bank Account'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mt: { xs: 2, md: 0 }, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                label={hasSalaryData ? 'Data Available' : 'No Data'}
                sx={{ 
                  bgcolor: hasSalaryData ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleSendPayslip}
                disabled={loading || !hasSalaryData || !selectedBankAccount}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                Send Payslip
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: '16px',
            border: '1px solid #e8ecf0',
            bgcolor: '#fff',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.9rem' }}>Bank Account</InputLabel>
                <Select
                  value={selectedBankAccount}
                  onChange={(e) => setSelectedBankAccount(e.target.value)}
                  label="Bank Account"
                  disabled={bankAccountsLoading}
                  sx={{
                    borderRadius: '10px',
                    bgcolor: '#f5f7fa',
                    '& .MuiSelect-select': { py: 1.2 },
                  }}
                >
                  {bankAccountsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading...
                    </MenuItem>
                  ) : (
                    bankAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.bankName} - {account.accountNumber}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Year"
                  sx={{
                    borderRadius: '10px',
                    bgcolor: '#f5f7fa',
                    '& .MuiSelect-select': { py: 1.2 },
                  }}
                >
                  {getYears().map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Month"
                  sx={{
                    borderRadius: '10px',
                    bgcolor: '#f5f7fa',
                    '& .MuiSelect-select': { py: 1.2 },
                  }}
                >
                  {getMonths().map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    borderColor: '#e0e0e0',
                    color: '#666',
                    '&:hover': {
                      borderColor: '#009db9',
                      color: '#009db9',
                    },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CalendarToday />}
                  onClick={() => {
                    setSelectedYear(currentYear);
                    setSelectedMonth(currentMonth);
                  }}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    bgcolor: '#009db9',
                    '&:hover': { bgcolor: '#008ba3' },
                  }}
                >
                  Current Month
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress sx={{ color: '#009db9' }} />
          </Box>
        ) : error ? (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
            <Typography color="error" sx={{ fontSize: '1.1rem' }}>{error}</Typography>
          </Card>
        ) : !selectedBankAccount ? (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
            <Typography variant="h6" gutterBottom>Select a Bank Account</Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a bank account to view salary metrics.
            </Typography>
          </Card>
        ) : metrics && hasSalaryData ? (
          <>
            {/* Metrics Cards Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {metricKeys.map((item, index) => {
                const key = item.key;
                const label = item.label;
                return (
                  <Grid item xs={6} sm={6} md={3} key={key}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        border: '1px solid #e8ecf0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        animation: `fadeInUp 0.5s ease ${index * 0.1}s both`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {label}
                          </Typography>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: getMetricBgColor(label),
                              color: getMetricColor(label),
                            }}
                          >
                            {getMetricIcon(label)}
                          </Avatar>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                          {formatCurrency(metrics[key])}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {formatPercentage(metrics[`${key}Percentage`])}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Chart and Table Section */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: '16px',
                    border: '1px solid #e8ecf0',
                    bgcolor: '#fff',
                    height: '100%',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                    Distribution
                  </Typography>
                  <Box sx={{ height: { xs: '280px', sm: '320px' }, width: '100%' }}>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={renderCustomizedLabel}
                            outerRadius={isSmallScreen ? 70 : 100}
                            innerRadius={isSmallScreen ? 30 : 40}
                            fill="#8884d8"
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend 
                            verticalAlign="bottom" 
                            height={36}
                            formatter={(value) => <span style={{ fontSize: '0.8rem' }}>{value}</span>}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">No data to display</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={7}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: '16px',
                    border: '1px solid #e8ecf0',
                    bgcolor: '#fff',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                    Detailed Breakdown
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: '#666', py: 1.5 }}>Metric</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#666', py: 1.5, textAlign: 'right' }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#666', py: 1.5, textAlign: 'right' }}>Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metricKeys.map((item) => {
                          const key = item.key;
                          const label = item.label;
                          return (
                            <TableRow key={key} hover>
                              <TableCell sx={{ py: 1, fontWeight: 500 }}>{label}</TableCell>
                              <TableCell sx={{ py: 1, textAlign: 'right', fontWeight: 600 }}>
                                {formatCurrency(metrics[key])}
                              </TableCell>
                              <TableCell sx={{ py: 1, textAlign: 'right' }}>
                                <Chip
                                  label={formatPercentage(metrics[`${key}Percentage`])}
                                  size="small"
                                  sx={{
                                    bgcolor: getMetricBgColor(label),
                                    color: getMetricColor(label),
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* CSS animation keyframes */}
            <style>
              {`
                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}
            </style>
          </>
        ) : (
          <Card sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: 'center', borderRadius: '16px' }}>
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
                No Salary Data Available
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                No salary information found for {formattedPeriod}. This could be because:
              </Typography>
              <Box sx={{ textAlign: 'left', color: '#666', fontSize: '0.9rem' }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>• Salary hasn't been processed for this period yet</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>• You haven't received earnings for this month</Typography>
                <Typography variant="body2">• Please try selecting a different month or contact payroll</Typography>
              </Box>
            </Box>
          </Card>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default SalaryMetricsChart;